import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, ArrowRight } from 'lucide-react';

const ManagerEvaluationsModal = ({
    isOpen,
    onClose,
    token,
    unitId,
    user,
    onEvaluate,
    onViewResult
}) => {
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'completed'

    useEffect(() => {
        if (isOpen && unitId && user?.areaid) {
            setLoading(true);
            fetch(`${process.env.REACT_APP_API_URL}/organograma/unit/${unitId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
            .then(res => res.json())
            .then(data => {
                if (!Array.isArray(data)) {
                    setEmployees([]);
                    return;
                }

                const filtered = data.filter(n => 
                    (n.area_id && n.area_id.toString() === user.areaid.toString()) ||
                    (n.areaid && n.areaid.toString() === user.areaid.toString())
                );

                const processed = filtered.filter(node => node.cargo).map(node => {
                    const hasFinishedCycle = node.evaluationHistory && Array.isArray(node.evaluationHistory) && node.evaluationHistory.length > 0;
                    
                    return {
                        id: node.id,
                        name: node.nome || 'Vaga em Aberto',
                        role: node.cargo,
                        status: hasFinishedCycle ? 'Concluído' : 'Pendente',
                        isVacant: !node.user_id,
                        photo: node.profile_picture_url,
                        nodeData: node,
                        hasFinishedCycle
                    };
                });

                setEmployees(processed);
            })
            .catch(err => console.error("Erro ao buscar avaliações:", err))
            .finally(() => setLoading(false));
        }
    }, [isOpen, unitId, user, token]);

    if (!isOpen) return null;

    const pendingEmployees = employees.filter(e => !e.hasFinishedCycle);
    const completedEmployees = employees.filter(e => e.hasFinishedCycle);

    const currentList = activeTab === 'pending' ? pendingEmployees : completedEmployees;

    // --- STYLES (Matching OrganogramPage) ---
    const overlayStyle = {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1300, backdropFilter: 'blur(4px)', padding: '24px'
    };

    const contentStyle = {
        background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        width: '95%', maxWidth: '800px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '85vh', overflow: 'hidden'
    };

    const headerStyle = {
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        padding: '24px 32px',
        borderBottom: '3px solid #f59e0b', // Warning color like Admin Page
        color: 'white',
        position: 'relative',
        flexShrink: 0
    };

    const closeButtonStyle = {
        position: 'absolute', top: '20px', right: '20px',
        background: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '8px',
        width: '36px', height: '36px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', transition: 'background 0.2s'
    };

    const tabContainerStyle = {
        display: 'flex', borderBottom: '1px solid #e2e8f0',
        backgroundColor: 'white', flexShrink: 0
    };

    const tabStyle = (isActive) => ({
        flex: 1, padding: '16px', textAlign: 'center', cursor: 'pointer',
        fontWeight: isActive ? '700' : '500',
        color: isActive ? '#1e293b' : '#64748b',
        borderBottom: isActive ? '3px solid #383838' : '3px solid transparent',
        backgroundColor: isActive ? '#f8fafc' : 'transparent',
        transition: 'all 0.2s'
    });

    const listContainerStyle = {
        padding: '24px', overflowY: 'auto', flexGrow: 1, backgroundColor: '#f8fafc'
    };

    const cardStyle = {
        backgroundColor: 'white', borderRadius: '12px', padding: '16px',
        marginBottom: '12px', border: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', gap: '16px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        transition: 'all 0.2s'
    };

    const buttonStyle = {
        padding: '10px 16px', borderRadius: '8px', border: 'none',
        fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '0.9rem', whiteSpace: 'nowrap'
    };

    const avatarStyle = {
        width: '48px', height: '48px', borderRadius: '50%',
        backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#64748b', fontWeight: 'bold', fontSize: '18px', overflow: 'hidden', flexShrink: 0
    };

    return (
        <div style={overlayStyle}>
            <div style={contentStyle}>
                <div style={headerStyle}>
                    <button onClick={onClose} style={closeButtonStyle}>
                        <X width={20} height={20} />
                    </button>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Minhas Avaliações</h2>
                    <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
                        Gerencie as avaliações da sua equipe.
                    </p>
                </div>

                <div style={tabContainerStyle}>
                    <div onClick={() => setActiveTab('pending')} style={tabStyle(activeTab === 'pending')}>
                        Pendentes ({pendingEmployees.length})
                    </div>
                    <div onClick={() => setActiveTab('completed')} style={tabStyle(activeTab === 'completed')}>
                        Concluídos ({completedEmployees.length})
                    </div>
                </div>

                <div style={listContainerStyle}>
                    {loading ? (
                        <p style={{ textAlign: 'center', color: '#64748b' }}>Carregando...</p>
                    ) : currentList.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                            {activeTab === 'pending' 
                                ? 'Nenhuma avaliação pendente.' 
                                : 'Nenhuma avaliação concluída.'}
                        </div>
                    ) : (
                        currentList.map(emp => (
                            <div key={emp.id} style={cardStyle}>
                                <div style={avatarStyle}>
                                    {emp.photo ? (
                                        <img src={emp.photo} alt={emp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        emp.name.charAt(0).toUpperCase()
                                    )}
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '1.05rem' }}>{emp.name}</div>
                                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>{emp.role}</div>
                                </div>
                                <div>
                                    {activeTab === 'pending' ? (
                                        <button
                                            onClick={() => onEvaluate(emp.nodeData.id)}
                                            style={{ ...buttonStyle, backgroundColor: '#f59e0b', color: 'white' }}
                                        >
                                            Avaliar <ArrowRight width={16} />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => onViewResult(emp.nodeData)}
                                            style={{ ...buttonStyle, backgroundColor: '#10b981', color: 'white' }}
                                        >
                                            Ver Resultado <CheckCircle2 width={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManagerEvaluationsModal;
